import { ConfigurationModel, defaultConfiguration } from '@tink/config/src/tinkLinkConfig';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ajaxRetry } from '../lib-api/ajaxRetry';
import { GetConfigurationResponse } from '../models/configuration';
import { base, createApiURL, ResponseObj } from '../lib-api/services/base';
import { ApplicationError } from '../components/ApplicationErrorBoundary/errors/ApplicationError';

export const ERR_FAILED_TO_FETCH_CONFIGURATION = {
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_CONFIGURATION,
  errorStatus: ErrorStatus.INTERNAL_ERROR,
};

export type ConfigurationClient = {
  getAppConfiguration: (
    userAccessToken: string
  ) => Promise<ResponseObj<GetConfigurationResponse> | ResponseObj<undefined>>;
  getConfigurationByMerchantId: (
    merchantId: string
  ) => Promise<ResponseObj<GetConfigurationResponse> | ResponseObj<undefined>>;
};

export type ConfigurationService = {
  getAppConfiguration: (userAccessToken: string, clientId: string) => Promise<ConfigurationModel>;
  getConfigurationByMerchantId: (merchantId: string) => Promise<ConfigurationModel | undefined>;
};

const configurationClient: ConfigurationClient = {
  getAppConfiguration: (userAccessToken) =>
    ajaxRetry({
      headers: { ...base.headers(), Authorization: `Bearer ${userAccessToken}` },
      url: createApiURL(`/link/v1/apps/configuration`),
    }),
  getConfigurationByMerchantId: (merchantId) =>
    ajaxRetry({
      headers: base.headers(),
      url: createApiURL(`/link/v1/merchant/${merchantId}/configuration`),
    }),
};

export const createConfigurationService = (client: ConfigurationClient): ConfigurationService => ({
  getConfigurationByMerchantId: async (merchantId) => {
    const configurationResponse = await client.getConfigurationByMerchantId(merchantId);

    if (!configurationResponse.isOk()) {
      throw new ApplicationError(ERR_FAILED_TO_FETCH_CONFIGURATION);
    }

    return configurationResponse.data?.configuration;
  },
  getAppConfiguration: async (userAccessToken: string, clientId: string) => {
    if (!clientId) {
      // There are pages which do not have `client_id` in query params (e.g. T&C page).
      // In those cases return default configuration.
      return defaultConfiguration;
    }

    const configurationResponse = await client.getAppConfiguration(userAccessToken);

    if (!configurationResponse.isOk() || !configurationResponse.data) {
      throw new ApplicationError(ERR_FAILED_TO_FETCH_CONFIGURATION);
    }

    // TODO: handle client errors, because using the default config might break customer flows
    if (configurationResponse.data?.configuration) {
      const fetchedConfiguration = configurationResponse.data.configuration;

      return fetchedConfiguration;
    }

    return defaultConfiguration;
  },
});

export const configurationService = createConfigurationService(configurationClient);
